import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, ChevronDown, Eye, EyeOff, LockKeyhole, Mail, Phone, UserRound } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthPageFrame } from '../../components/auth/AuthPageFrame';
import { useAuth } from '../../hooks/useAuth';
import { normalizePhoneNumber } from '../../utils/validators';
import { phoneCountries } from '../../data/phoneCountries';
import { countryCurrencies } from '../../data/countryCurrencies';
import { sendWelcomeCommunications } from '../../services/communicationService';

const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Nairobi';

export function RegisterPage() {
  const showcaseVideo = 'https://res.cloudinary.com/ubunifu/video/upload/v1607186910/pexels-rodnae-productions-5923291_jd1rwl.mp4';
  const { register } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState({ displayName: '', email: '', phone: '', phoneCountry: 'Kenya', phoneCountryCode: 'KE', countryDialCode: '254', currency: 'KES', timezone: browserTimezone, password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [detectedCountryCode, setDetectedCountryCode] = useState('KE');
  const hasSelectedCountry = useRef(false);

  useEffect(() => {
    let cancelled = false;
    fetch('https://us-central1-smart-campus-africa.cloudfunctions.net/detectVisitorCountry')
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Country detection failed'))))
      .then(({ countryCode }) => {
        if (cancelled || hasSelectedCountry.current || !countryCode) return;
        const detectedCountry = phoneCountries.find(([code]) => code === countryCode);
        if (!detectedCountry) return;
        const [phoneCountryCode, phoneCountry, countryDialCode] = detectedCountry;
        setDetectedCountryCode(countryCode);
        setValues((current) => ({ ...current, phoneCountry, phoneCountryCode, countryDialCode, currency: countryCurrencies[phoneCountryCode] || 'USD' }));
      })
      .catch(() => {
        // Kenya remains the safe default when geolocation is unavailable.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const orderedPhoneCountries = [
    ...phoneCountries.filter(([code]) => code === detectedCountryCode),
    ...phoneCountries.filter(([code]) => code !== detectedCountryCode),
  ];

  async function onSubmit(event) {
    event.preventDefault();
    setError('');
    if (values.password !== values.confirmPassword) {
      setError('Passwords must match.');
      return;
    }
    const normalizedPhone = normalizePhoneNumber(values.phone, values.countryDialCode);
    if (!normalizedPhone || normalizedPhone.length < 8) {
      setError('Enter a valid phone number.');
      return;
    }
    if (!acceptedTerms) {
      setError('Please accept the Terms of Service and Data Processing Addendum to continue.');
      return;
    }
    setLoading(true);
    try {
      const createdUser = await register({ ...values, phone: normalizedPhone });
      try {
        await sendWelcomeCommunications({ user: createdUser, phone: normalizedPhone });
      } catch (communicationError) {
        console.error('Welcome communications could not be sent', communicationError);
      }
      navigate('/setup', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthPageFrame
      eyebrow="Create your workspace"
      title="Start building your academy."
      description="Set up your SmartCampus account in less than a minute."
      step="Step 1 of 2"
      showcaseVideo={showcaseVideo}
      className="smart-register-page"
      footer={<><span>Already have an account?</span><Link to="/login">Log in</Link></>}
    >
      <form className="smart-auth-form smart-auth-form--register" onSubmit={onSubmit}>
        {error ? <div className="smart-auth-alert" role="alert">{error}</div> : null}
        <label className="smart-auth-field" htmlFor="displayName">
          <span>Your name</span>
          <div><UserRound size={18} aria-hidden="true" /><input id="displayName" autoComplete="name" placeholder="Full name" required value={values.displayName} onChange={(event) => setValues({ ...values, displayName: event.target.value })} /></div>
        </label>
        <label className="smart-auth-field" htmlFor="email">
          <span>Email</span>
          <div><Mail size={18} aria-hidden="true" /><input id="email" type="email" autoComplete="email" placeholder="name@example.com" required value={values.email} onChange={(event) => setValues({ ...values, email: event.target.value })} /></div>
        </label>
        <label className="smart-auth-field" htmlFor="phone">
          <span>Phone number</span>
          <div className="smart-auth-phone-field">
            <Phone size={18} aria-hidden="true" />
            <span className="smart-auth-country-wrap">
              <select
                id="phone-country"
                aria-label="Phone country"
                value={`${values.phoneCountryCode}|${values.phoneCountry}|${values.countryDialCode}`}
                onChange={(event) => {
                  const [phoneCountryCode, phoneCountry, countryDialCode] = event.target.value.split('|');
                  hasSelectedCountry.current = true;
                  setValues({ ...values, phoneCountryCode, phoneCountry, countryDialCode, currency: countryCurrencies[phoneCountryCode] || 'USD' });
                }}
              >
                {orderedPhoneCountries.map(([code, name, dialCode]) => <option key={`${code}-${dialCode}`} value={`${code}|${name}|${dialCode}`}>{name} (+{dialCode})</option>)}
              </select>
              <ChevronDown size={14} aria-hidden="true" />
            </span>
            <input id="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="712 345 678" required value={values.phone} onChange={(event) => setValues({ ...values, phone: event.target.value })} />
          </div>
        </label>
        <label className="smart-auth-field" htmlFor="password">
          <span>Create a password</span>
          <div><LockKeyhole size={18} aria-hidden="true" /><input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="At least 6 characters" required minLength="6" value={values.password} onChange={(event) => setValues({ ...values, password: event.target.value })} /><button className="smart-auth-password-toggle" type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((visible) => !visible)}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>
        </label>
        <label className="smart-auth-field" htmlFor="confirmPassword">
          <span>Confirm password</span>
          <div><Check size={18} aria-hidden="true" /><input id="confirmPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Repeat your password" required value={values.confirmPassword} onChange={(event) => setValues({ ...values, confirmPassword: event.target.value })} /></div>
        </label>
        <label className="smart-auth-consent"><input type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} /> <span>I agree to the <Link to="/terms-of-service">Terms of Service</Link> and <Link to="/data-processing-addendum">Data Processing Addendum</Link>.</span></label>
        <button className="smart-auth-submit" type="submit" disabled={loading || !acceptedTerms}>{loading ? 'Creating your account…' : <>Create account <ArrowRight size={19} /></>}</button>
      </form>
    </AuthPageFrame>
  );
}
